/**
 * useBackgroundOptimization Hook
 * Optimizes background image loading with lazy loading, preloading, and caching
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { ResponsiveBackgroundImage } from '@/types/sections';
import { mediaCache } from '@/lib/media-cache';

export interface BackgroundOptimizationOptions {
  /**
   * Whether to enable lazy loading (default: true)
   */
  lazyLoad?: boolean;
  
  /**
   * Whether this background is critical and should be preloaded (default: false)
   */
  priority?: boolean;
  
  /**
   * Root margin for Intersection Observer (default: '200px')
   */
  rootMargin?: string;
  
  /**
   * Threshold for Intersection Observer (default: 0.1)
   */
  threshold?: number;
  
  /**
   * Breakpoint for responsive images (default: 1024px)
   */
  breakpoint?: number;
}

export interface BackgroundOptimizationResult {
  /**
   * The URL to use for the background (responsive-aware)
   */
  url: string | undefined;
  
  /**
   * Whether the background is currently visible
   */
  isVisible: boolean;
  
  /**
   * Whether the background is loaded
   */
  isLoaded: boolean;
  
  /**
   * Whether to use desktop or mobile version
   */
  isDesktop: boolean;
  
  /**
   * Preload the background image
   */
  preload: () => void;
  
  /**
   * Clear the background cache
   */
  clearCache: () => void;
}

// Use unified media cache instead of separate cache
// Keeping for backward compatibility but delegating to unified cache

/**
 * Hook for optimizing background image loading
 */
export function useBackgroundOptimization(
  background: ResponsiveBackgroundImage | string | undefined,
  options: BackgroundOptimizationOptions = {}
): BackgroundOptimizationResult {
  const {
    lazyLoad = true,
    priority = false,
    rootMargin = '200px',
    threshold = 0.1,
    breakpoint = 1024,
  } = options;

  const [isVisible, setIsVisible] = useState(!lazyLoad || priority);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= breakpoint;
  });

  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Determine the URL to use based on viewport
  const url = useMemo(() => {
    if (!background) return undefined;

    if (typeof background === 'string') {
      return background;
    }

    // Responsive background
    if (isDesktop) {
      return background.desktop || background.mobile;
    } else {
      return background.mobile || background.desktop;
    }
  }, [background, isDesktop]);

  // Handle viewport changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
    
    const updateIsDesktop = () => {
      setIsDesktop(mediaQuery.matches);
    };
    
    updateIsDesktop();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateIsDesktop);
      return () => mediaQuery.removeEventListener('change', updateIsDesktop);
    } else {
      // Fallback for older browsers
      const handler = () => updateIsDesktop();
      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    }
  }, [breakpoint]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazyLoad || priority || !elementRef.current) {
      setIsVisible(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (observerRef.current) {
              observerRef.current.disconnect();
            }
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazyLoad, priority, rootMargin, threshold]);

  // Preload image when visible - use unified cache
  useEffect(() => {
    if (!isVisible || !url || isLoaded) return;
    
    // Check unified cache first
    const checkCache = async () => {
      const isCached = await mediaCache.isInBrowserCache(url);
      if (isCached) {
        setIsLoaded(true);
        return;
      }
      
      // Not cached - check if already preloaded
      if (mediaCache.isPreloaded(url)) {
        setIsLoaded(true);
        return;
      }
      
      // Preload using unified cache
      mediaCache.preloadMedia(url, priority ? 'high' : 'low').then((success) => {
        if (success) {
          setIsLoaded(true);
        }
      });
    };
    
    checkCache();
  }, [isVisible, url, isLoaded, priority]);

  // Preload function - use unified cache
  const preload = useCallback(async () => {
    if (!url) return;
    
    // Check if already cached/preloaded
    const isCached = await mediaCache.isInBrowserCache(url);
    if (isCached || mediaCache.isPreloaded(url)) {
      setIsLoaded(true);
      return;
    }
    
    // Preload using unified cache
    const success = await mediaCache.preloadMedia(url, priority ? 'high' : 'low');
    if (success) {
      setIsLoaded(true);
    }
  }, [url, priority]);

  // Clear cache function - unified cache doesn't support per-item clear
  // Just reset loaded state
  const clearCache = useCallback(() => {
    setIsLoaded(false);
  }, []);

  // Set element ref for intersection observer
  const setElementRef = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
  }, []);

  return {
    url,
    isVisible,
    isLoaded,
    isDesktop,
    preload,
    clearCache,
    setElementRef,
  } as BackgroundOptimizationResult & { setElementRef: (element: HTMLElement | null) => void };
}

/**
 * Hook for managing multiple background optimizations
 */
export function useBackgroundOptimizations(
  backgrounds: Array<ResponsiveBackgroundImage | string | undefined>,
  options: BackgroundOptimizationOptions = {}
): BackgroundOptimizationResult[] {
  return backgrounds.map((bg) => useBackgroundOptimization(bg, options));
}

/**
 * Preload critical backgrounds
 */
export function preloadBackgrounds(
  backgrounds: Array<ResponsiveBackgroundImage | string | undefined>,
  isDesktop = true
): void {
  backgrounds.forEach((bg) => {
    if (!bg) return;

    let url: string | undefined;
    if (typeof bg === 'string') {
      url = bg;
    } else {
      url = isDesktop ? bg.desktop || bg.mobile : bg.mobile || bg.desktop;
    }

    if (url && !mediaCache.isPreloaded(url)) {
      mediaCache.preloadMedia(url, 'low').catch(() => {
        // Silently handle errors
      });
    }
  });
}

/**
 * Clear all preload cache - use unified cache
 */
export function clearPreloadCache(): void {
  mediaCache.clearCache();
}

